import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { storesAPI, locationsAPI, Store, State, City } from '../api';
import { getCurrentLocation, findLocationIds } from '../utils/geolocation';
import { createSlug } from '../utils/slug';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { LISTING_CATEGORY_KEYS, normalizeListingCategory, resolveListingCategory } from '../constants/listingCategories';
import {
  DEFAULT_CLOSE_TIME,
  DEFAULT_OPEN_TIME,
  defaultSellerHours,
  sellerHoursToJson,
  SellerHoursMap,
} from '../utils/sellerHours';

// Store category options (matches listing categories) + Other for custom
const STORE_CATEGORIES = [...LISTING_CATEGORY_KEYS];

const formatCategoryLabel = (value: string) =>
  value.split(/[+/]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const OTHER_CATEGORY_VALUE = '__other__';

const MyStores: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [storeAddress, setStoreAddress] = useState('');
  const pendingCityIdRef = useRef<number | null>(null);
  const pendingCityNameRef = useRef<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    category_select: '',
    product_type: '',
    hours: defaultSellerHours(),
    state_id: '',
    city_id: '',
    returns_money_back: false,
    website: '',
    tags: '',
  });

  useEffect(() => {
    loadStores();
    loadStates();
  }, []);

  useEffect(() => {
    if (formData.state_id) {
      const stateIdNum = parseInt(formData.state_id);
      if (!isNaN(stateIdNum)) {
        loadCities(stateIdNum);
      }
    } else {
      setCities([]);
      pendingCityIdRef.current = null;
      pendingCityNameRef.current = null;
    }
  }, [formData.state_id]);

  // Match city after cities are loaded
  useEffect(() => {
    if (cities.length > 0 && formData.state_id && !formData.city_id) {
      if (pendingCityIdRef.current !== null) {
        const cityId = pendingCityIdRef.current.toString();
        setFormData(prev => ({ ...prev, city_id: cityId }));
        pendingCityIdRef.current = null;
        pendingCityNameRef.current = null;
      } else if (pendingCityNameRef.current !== null) {
        const cityName = pendingCityNameRef.current.toLowerCase().trim();
        let matchedCity = cities.find((c: City) => {
          const dbCityName = c.name.toLowerCase().trim();
          return dbCityName === cityName;
        });
        
        if (!matchedCity) {
          matchedCity = cities.find((c: City) => {
            const dbCityName = c.name.toLowerCase().trim();
            return dbCityName.includes(cityName) || cityName.includes(dbCityName);
          });
        }
        
        if (matchedCity && matchedCity.id) {
          setFormData(prev => ({ ...prev, city_id: matchedCity!.id.toString() }));
        } else if (pendingCityNameRef.current && formData.state_id) {
          // City not found, try to add it to the database
          const cityNameToAdd = pendingCityNameRef.current;
          const stateIdStr = formData.state_id;
          
          // Use async function inside useEffect
          (async () => {
            try {
              const stateIdNum = parseInt(stateIdStr);
              if (!isNaN(stateIdNum)) {
                console.log('Attempting to add city to database:', cityNameToAdd, 'state_id:', stateIdNum);
                const newCity = await locationsAPI.addCity(cityNameToAdd, stateIdNum);
                console.log('City added to database:', newCity);
                
                // Reload cities to include the new one
                const updatedCities = await locationsAPI.getCitiesByState(stateIdNum);
                setCities(updatedCities);
                
                // Set the city_id to the newly created city
                if (newCity && newCity.id) {
                  setFormData(prev => ({ ...prev, city_id: newCity.id.toString() }));
                  toast.success(`Added "${cityNameToAdd}" to the database`);
                }
              }
            } catch (addCityErr: any) {
              console.error('Error adding city to database:', addCityErr);
              toast.error(`Could not add city "${cityNameToAdd}". Please select a city from the dropdown.`);
            }
          })();
        }
        pendingCityIdRef.current = null;
        pendingCityNameRef.current = null;
      }
    }
  }, [cities, formData.state_id, formData.city_id]);

  const loadStores = async () => {
    try {
      const data = await storesAPI.getMyStores();
      setStores(data);
    } catch (err: any) {
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const loadStates = async () => {
    try {
      const data = await locationsAPI.getStates();
      setStates(data);
    } catch (err) {
      console.error('Failed to load states:', err);
    }
  };

  const loadCities = async (stateId: number) => {
    try {
      const data = await locationsAPI.getCitiesByState(stateId);
      setCities(data);
    } catch (err) {
      console.error('Failed to load cities:', err);
    }
  };

  const detectLocation = async () => {
    setLocationLoading(true);
    try {
      const location = await getCurrentLocation();
      
      if (location.stateAbbreviation) {
        const { stateId, cityId } = await findLocationIds(
          location.city || '',
          location.stateAbbreviation
        );
        
        if (stateId) {
          setFormData(prev => ({ ...prev, state_id: stateId.toString() }));
          await loadCities(stateId);
          if (cityId) {
            setFormData(prev => ({ ...prev, city_id: cityId.toString() }));
          }
        }
      }
    } catch (err) {
      console.error('Location detection failed:', err);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const addressToUse = (formData.address || storeAddress || '').trim();
    if (!formData.name || !addressToUse || !formData.city_id || !formData.state_id) {
      setError('Name, address, state, and city are required');
      return;
    }

    setSubmitting(true);
    try {
      // Convert hours to JSON string
      const hoursJson = sellerHoursToJson(formData.hours as SellerHoursMap);
      const productType =
        formData.category_select === OTHER_CATEGORY_VALUE
          ? (formData.product_type?.trim() ? normalizeListingCategory(formData.product_type) : undefined)
          : formData.category_select
            ? normalizeListingCategory(formData.category_select)
            : undefined;

      if (editingStore) {
        // Update existing store
        await storesAPI.update(editingStore.id, {
          name: formData.name,
          address: addressToUse,
          hours: hoursJson,
          returns_money_back: formData.returns_money_back,
          product_type: productType,
          website: formData.website.trim(),
          tags: formData.tags.trim(),
        });
        setEditingStore(null);
      } else {
        // Create new store
        await storesAPI.create({
          name: formData.name,
          address: addressToUse,
          hours: hoursJson,
          city_id: parseInt(formData.city_id),
          state_id: parseInt(formData.state_id),
          returns_money_back: formData.returns_money_back,
          product_type: productType,
          website: (formData.website || '').trim() || undefined,
          tags: (formData.tags || '').trim() || undefined,
        });
      }
      setShowCreateForm(false);
      setEditingStore(null);
      setStoreAddress('');
      setFormData({
        name: '',
        address: '',
        category_select: '',
        product_type: '',
        hours: defaultSellerHours(),
        state_id: '',
        city_id: '',
        returns_money_back: false,
        website: '',
        tags: '',
      });
      loadStores();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${editingStore ? 'update' : 'create'} store`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (store: Store) => {
    setEditingStore(store);
    let hours: SellerHoursMap = defaultSellerHours();
    
    // Parse hours if they exist
    if (store.hours) {
      try {
        const parsedHours = typeof store.hours === 'string' ? JSON.parse(store.hours) : store.hours;
        hours = { ...hours, ...parsedHours };
      } catch (e) {
        console.error('Error parsing hours:', e);
      }
    }
    
    setStoreAddress(store.address || '');
    setFormData({
      name: store.name,
      address: store.address,
      category_select: (() => {
        const resolved = store.product_type ? resolveListingCategory(store.product_type) : '';
        return resolved && STORE_CATEGORIES.includes(resolved)
          ? resolved
          : store.product_type
            ? OTHER_CATEGORY_VALUE
            : '';
      })(),
      product_type: store.product_type || '',
      hours,
      state_id: store.state_id?.toString() || '',
      city_id: store.city_id?.toString() || '',
      returns_money_back: store.returns_money_back || false,
      website: store.website || '',
      tags: store.tags || '',
    });
    if (store.state_id) {
      loadCities(store.state_id);
    }
    setShowCreateForm(true);
  };

  const handleDelete = async (storeId: number) => {
    if (!window.confirm('Are you sure you want to delete this store? This will not delete listings associated with it.')) {
      return;
    }

    try {
      await storesAPI.delete(storeId);
      loadStores();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete store');
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading stores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Stores</h1>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-2 rounded-lg transition"
          >
            {showCreateForm ? 'Cancel' : '+ Create Store'}
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">{editingStore ? 'Edit Store' : 'Create New Store'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Store Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="My Awesome Store"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category_select}
                  onChange={(e) => setFormData({
                    ...formData,
                    category_select: e.target.value,
                    product_type: e.target.value === OTHER_CATEGORY_VALUE ? formData.product_type : e.target.value,
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select category</option>
                  {STORE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {formatCategoryLabel(cat)}
                    </option>
                  ))}
                  <option value={OTHER_CATEGORY_VALUE}>Other (type below)</option>
                </select>
                {formData.category_select === OTHER_CATEGORY_VALUE && (
                  <input
                    type="text"
                    value={formData.product_type}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    placeholder="Category not listed"
                    className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="url"
                  inputMode="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                  placeholder="https://yourstore.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
                  placeholder="e.g. organic, handmade, local — comma separated"
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1">Separate tags with commas. Shoppers use these to find your store.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Store Address <span className="text-red-500">*</span>
                </label>
                <LocationAutocomplete
                  value={storeAddress}
                  onInputChange={(value) => {
                    setStoreAddress(value);
                    setFormData((prev) => ({ ...prev, address: value }));
                  }}
                  onPlaceSelect={async (place) => {
                    try {
                      console.log('Location selected for store:', place);
                      setStoreAddress(place.address);
                      setFormData(prev => ({ ...prev, address: place.address }));
                      
                      // Find matching state and city IDs
                      const { stateId, cityId } = await findLocationIds(
                        place.city,
                        place.stateAbbreviation
                      );
                      
                      console.log('Store location IDs found:', { stateId, cityId, cityName: place.city });
                      
                      if (stateId != null && stateId !== undefined) {
                        // Ensure city exists; create if missing
                        let finalCityId = cityId;
                        if ((finalCityId == null || finalCityId === undefined) && place.city) {
                          try {
                            const newCity = await locationsAPI.addCity(place.city, stateId);
                            if (newCity && newCity.id) {
                              finalCityId = newCity.id;
                              // Reload cities for the state so the new city appears
                              const updatedCities = await locationsAPI.getCitiesByState(stateId);
                              setCities(updatedCities);
                            }
                          } catch (addErr) {
                            console.error('Failed to add city to database:', addErr);
                          }
                        }
                        if (finalCityId != null && finalCityId !== undefined) {
                          pendingCityIdRef.current = finalCityId;
                        }
                        if (place.city) {
                          pendingCityNameRef.current = place.city;
                        }
                        
                        // Update state_id - this will trigger useEffect to load cities
                        const stateIdStr = String(stateId);
                        setFormData(prev => ({
                          ...prev,
                          state_id: stateIdStr,
                          city_id: finalCityId ? String(finalCityId) : '' // If created/found, set immediately
                        }));
                      }
                    } catch (error) {
                      console.error('Error updating store location:', error);
                      pendingCityIdRef.current = null;
                      pendingCityNameRef.current = null;
                    }
                  }}
                  placeholder="Enter store address (address, city, state)"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Start typing your address and select from suggestions</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      State *
                    </label>
                    <button
                      type="button"
                      onClick={detectLocation}
                      disabled={locationLoading}
                      className="text-xs text-green-600 hover:text-green-700 font-medium disabled:opacity-50"
                    >
                      {locationLoading ? '📍 Detecting...' : '📍 Use My Location'}
                    </button>
                  </div>
                  <select
                    required
                    value={formData.state_id}
                    onChange={(e) => {
                      setFormData({ ...formData, state_id: e.target.value, city_id: '' });
                      if (e.target.value) {
                        loadCities(parseInt(e.target.value));
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <select
                    required
                    value={formData.city_id}
                    onChange={(e) => setFormData({ ...formData, city_id: e.target.value })}
                    disabled={!formData.state_id}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                  >
                    <option value="">Select City</option>
                    {cities
                      .map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Store Hours
                </label>
                <div className="space-y-3">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                    <div key={day} className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg">
                      <div className="w-24">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!formData.hours[day as keyof typeof formData.hours].closed}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                hours: {
                                  ...formData.hours,
                                  [day]: {
                                    ...formData.hours[day as keyof typeof formData.hours],
                                    closed: !e.target.checked,
                                    open: e.target.checked
                                      ? formData.hours[day as keyof typeof formData.hours].open || DEFAULT_OPEN_TIME
                                      : '',
                                    close: e.target.checked
                                      ? formData.hours[day as keyof typeof formData.hours].close || DEFAULT_CLOSE_TIME
                                      : '',
                                  },
                                },
                              });
                            }}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-gray-700">{day.substring(0, 3)}</span>
                        </label>
                      </div>
                      {!formData.hours[day as keyof typeof formData.hours].closed ? (
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Open</label>
                            <input
                              type="time"
                              value={formData.hours[day as keyof typeof formData.hours].open}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  hours: {
                                    ...formData.hours,
                                    [day]: {
                                      ...formData.hours[day as keyof typeof formData.hours],
                                      open: e.target.value,
                                    },
                                  },
                                });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Close</label>
                            <input
                              type="time"
                              value={formData.hours[day as keyof typeof formData.hours].close}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  hours: {
                                    ...formData.hours,
                                    [day]: {
                                      ...formData.hours[day as keyof typeof formData.hours],
                                      close: e.target.value,
                                    },
                                  },
                                });
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1">
                          <span className="text-sm text-gray-400 italic">Closed</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Set different hours for each day or mark days as closed</p>
              </div>

              <div>
                <label className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.returns_money_back}
                    onChange={(e) => setFormData({ ...formData, returns_money_back: e.target.checked })}
                    className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <div>
                    <span className="font-semibold text-gray-900">↩️ Returns, Money Back</span>
                    <p className="text-xs text-gray-500 mt-1">Offer returns and money back guarantee for items from this store</p>
                  </div>
                </label>
              </div>

              <div className="flex space-x-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition flex items-center justify-center gap-2"
                  >
                    {submitting && (
                      <span
                        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                        aria-hidden
                      />
                    )}
                    {submitting
                      ? editingStore
                        ? 'Updating…'
                        : 'Creating…'
                      : editingStore
                        ? 'Update Store'
                        : 'Create Store'}
                  </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingStore(null);
                    setFormData({
                      name: '',
                      address: '',
                      category_select: '',
                      product_type: '',
                      hours: defaultSellerHours(),
                      state_id: '',
                      city_id: '',
                      returns_money_back: false,
                      website: '',
                      tags: '',
                    });
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {stores.length === 0 && !showCreateForm ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">You don't have any stores yet.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-2 rounded-lg transition"
            >
              Create Your First Store
            </button>
          </div>
        ) : (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Your Stores ({stores.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stores.map((store) => (
                <div key={store.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
                  <div className="mb-4">
                    <Link to={`/stores/${createSlug(store.name)}`}>
                      <h3 className="text-xl font-bold text-gray-900 hover:text-green-600 transition mb-2">
                        <i className="ri-store-line mr-1"></i> {store.name}
                      </h3>
                    </Link>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="flex items-center min-w-0">
                        <span className="mr-2 flex-shrink-0">📍</span>
                        <span className="truncate overflow-hidden min-w-0">{store.address}</span>
                      </p>
                      <p className="flex items-center">
                        <span className="mr-2">🏙️</span>
                        {store.city_name}, {store.state_abbreviation}
                      </p>
                      {store.close_time && (
                        <p className="flex items-center">
                          <span className="mr-2">🕐</span>
                          Closes at: {formatTime(store.close_time)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 pt-4 border-t">
                    <Link
                      to={`/stores/${createSlug(store.name)}`}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white text-center font-medium py-2 px-4 rounded-lg transition text-sm"
                    >
                      View Profile
                    </Link>
                    <button
                      onClick={() => handleEdit(store)}
                      className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(store.id)}
                      className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyStores;

